'use client';

import { useCallback } from 'react';
import { MarketId } from '@velocity-exchange/common';
import useVelocityStore from '../stores/VelocityStore/useVelocityStore';
import useVelocityClientIsReady from './useVelocityClientIsReady';
import useUiUpdateInterval from './useUiUpdateInterval';
import { usePerpMarketsLookup } from './perpMarkets/usePerpMarketsLookup';
import { publishPerpMarketInfo } from 'src/stores/marketLiveState';

/**
 * Perp-market-account transport adapter for the live-state module (stage 4).
 *
 * Publishes each perp market's on-chain account (open interest, funding period,
 * status, ...) — paired with the slot it was observed at — into the module's
 * stats slice, so the market-info display derivation (`useLiveMarketInfoDisplay`)
 * reads a module slice instead of pulling raw velocityClient accounts itself.
 * OI / funding period move slowly, so the essential UI cadence (~1s) is ample;
 * the module arbitrates republishes by slot (highest-slot-wins). The chain-head
 * slot used for oracle slot-delay comes from `useSlotQuery`, not from here.
 */
const usePerpMarketInfoSync = () => {
	const velocityClientIsReady = useVelocityClientIsReady();
	const velocityClient = useVelocityStore((s) => s.velocityClient.client);
	const perpMarketsLookup = usePerpMarketsLookup();

	const syncPerpMarketInfo = useCallback(() => {
		if (!velocityClientIsReady || !velocityClient) return;

		perpMarketsLookup.forEach((config) => {
			if (!config) return;

			let accountAndSlot;
			try {
				accountAndSlot =
					velocityClient.accountSubscriber.getMarketAccountAndSlot(
						config.marketIndex
					);
			} catch {
				return;
			}
			if (!accountAndSlot) return;

			publishPerpMarketInfo(
				MarketId.createPerpMarket(config.marketIndex),
				accountAndSlot.data,
				accountAndSlot.slot
			);
		});
	}, [velocityClient, velocityClientIsReady, perpMarketsLookup]);

	useUiUpdateInterval(syncPerpMarketInfo, true, true);
};

export default usePerpMarketInfoSync;
